import React from "react";
import { motion } from "framer-motion";
import { fleetData, heroData } from "../data/mock";
import { Check, Users, MessageCircle, Star, ShieldCheck } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

const rateMap = {
  Sedan: { local: "₹10/km", outstation: "₹11/km", airport: "₹900" },
  "SUV / MUV": { local: "₹15/km", outstation: "₹16/km", airport: "₹1400" },
  Premium: { local: "₹12/km", outstation: "₹13/km", airport: "₹1500" },
};

const FleetSection = () => {
  const handleBookVehicle = (vehicle) => {
    const message = encodeURIComponent(
      `Hello Bahubali Cabs! I want to book a *${vehicle.type}* (${vehicle.models}). Please confirm availability.`,
    );
    window.open(`https://wa.me/${heroData.whatsapp}?text=${message}`, "_blank");
  };

  return (
    <section id="fleet" className="py-24 bg-slate-50 relative overflow-hidden">
      {/* 🔹 Background Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 via-transparent to-amber-500 opacity-20" />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 text-amber-600 font-black uppercase tracking-widest text-xs mb-4"
          >
            <ShieldCheck className="w-4 h-4" /> Best Price Guaranteed
          </motion.div>
          <h2 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tighter mb-6">
            Our Premium <span className="text-amber-500">Fleet</span>
          </h2>
          <p className="text-slate-500 text-lg md:text-xl max-w-2xl mx-auto font-medium">
            Choose from our wide range of well-maintained vehicles for a
            comfortable journey across Rajasthan.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {fleetData.map((vehicle, index) => {
            const rates = rateMap[vehicle.type] || {};
            return (
              <motion.div
                key={vehicle.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group h-full bg-white border-none shadow-2xl shadow-slate-200/60 rounded-[2.5rem] overflow-hidden flex flex-col hover:shadow-amber-500/10 transition-all duration-500">
                  {/* Vehicle Image Container */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={vehicle.image}
                      alt={vehicle.type}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-5 right-5">
                      <div className="bg-white/90 backdrop-blur-md px-4 py-2 rounded-2xl flex items-center gap-1 shadow-sm">
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <span className="text-xs font-black text-slate-900">
                          4.9/5
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-8 flex flex-col flex-1">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-2xl font-black text-slate-900 uppercase italic">
                          {vehicle.type}
                        </h3>
                        <p className="text-amber-600 font-bold text-sm tracking-wide">
                          {vehicle.models}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-bold text-slate-600">
                          {vehicle.capacity}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3 mb-8">
                      {vehicle.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          <span className="text-sm font-semibold text-slate-500">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Pricing Grid */}
                    <div className="grid grid-cols-2 gap-2 mb-8">
                      <RateBadge label="Local" rate={rates.local} />
                      <RateBadge label="Outstation" rate={rates.outstation} />
                      <div className="col-span-2">
                        <RateBadge
                          label="Airport Transfer"
                          rate={rates.airport}
                          full
                        />
                      </div>
                    </div>

                    <Button
                      onClick={() => handleBookVehicle(vehicle)}
                      className="w-full py-7 rounded-2xl bg-slate-900 hover:bg-amber-500 text-white hover:text-slate-900 font-black text-lg transition-all duration-300 group/btn"
                    >
                      <MessageCircle className="w-5 h-5 mr-2 group-hover/btn:animate-bounce" />
                      BOOK NOW
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const RateBadge = ({ label, rate, full }) => (
  <div
    className={`p-3 bg-slate-50 rounded-2xl border border-slate-100 ${full ? "flex justify-between items-center" : ""}`}
  >
    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
      {label}
    </p>
    <p className="text-sm font-black text-slate-900">{rate || "On Call"}</p>
  </div>
);

export default FleetSection;
